import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Megaphone, X } from 'lucide-react';
import { isTopBannerRoute } from '@/components/notifications/InAppAnnouncementHost';
import { usePortalCampaigns } from './usePortalCampaigns';
import { campaignBodyText, renderableButtons } from '@/lib/campaigns/portalCampaigns';
import { campaignTypeLabel } from '@/lib/admin/messageCampaigns';
import { useCampaignCountdown } from './useCampaignCountdown';

export function PortalCampaignBanner() {
  const { pathname } = useLocation();
  const { enabled, active, dismiss, activate, markSeen } = usePortalCampaigns();
  const onBannerRoute = isTopBannerRoute(pathname);
  const campaign = enabled && onBannerRoute && active?.placement === 'top_banner' ? active : null;
  const countdown = useCampaignCountdown(campaign?.expiresAt ?? null);

  useEffect(() => {
    markSeen(campaign);
  }, [campaign, markSeen]);

  if (!campaign) return null;

  const body = campaignBodyText(campaign.message, campaign.bodyFormat);
  const buttons = renderableButtons(campaign.buttons);

  return (
    <div className="flex items-center gap-3 bg-gradient-to-r from-brand-cta to-brand px-4 py-2.5 text-white shadow-[0_2px_10px_rgba(16,24,40,0.12)] sm:px-6 lg:px-8">
      <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 sm:flex">
        <Megaphone className="h-4.5 w-4.5" />
      </span>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
          {campaignTypeLabel(campaign)}
        </span>
        <span className="truncate text-[13.5px] font-semibold">{campaign.title}</span>
        {body && (
          <span className="hidden min-w-0 truncate text-[12.5px] text-white/85 md:inline">
            — {body}
          </span>
        )}
      </div>

      {countdown && (
        <span className="hidden shrink-0 rounded-md bg-black/20 px-2 py-1 text-[11.5px] font-semibold tabular-nums sm:inline-block">
          Ends in {countdown}
        </span>
      )}

      {buttons.map((button, index) => (
        <button
          key={`${button.url}-${index}`}
          type="button"
          onClick={() => activate(campaign, button)}
          className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-[12.5px] font-semibold text-brand-cta transition hover:bg-white/90"
        >
          {button.label}
        </button>
      ))}

      <button
        type="button"
        onClick={() => dismiss(campaign)}
        aria-label="Dismiss"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/15 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default PortalCampaignBanner;
